import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ClientProfile, CoachInterface } from "../../../interfaces/common";
import {Modal, ModalHeader, ModalBody, ModalFooter} from "../../../components/Modal";
import {CardBody, CardFooter, CustomCard} from "../../../components/CustomCard";
import CustomButton from "../../../components/CustomButton";
import TextLink from "../../../components/TextLink";
import CoachMatching from "../../../components/CoachMatching/CoachMatching";
import Api from "../../../services/Api";
import iconAccount from "url:shared-assets/icons/account.svg";
import MaskedIcon from "../../../components/MaskedIcon";
import iconPlus from "url:shared-assets/icons/plus.svg";
import chevronLeftIcon from "url:shared-assets/icons/chevronLeft.svg";
import badgeCheckIcon from "url:shared-assets/icons/badge-check.svg";
import { getLanguage } from "../../../lang/helpers";

interface Props {
  profile: ClientProfile;
}

enum ConfirmStatus {
  None,
  Done,
}

const StepSeven: React.FC<Props> = (props: Props) => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = getLanguage(i18n.language);
  const [selectCoach, setSelectCoach] = useState<boolean>(false);
  const [showCoach, setShowCoach] = useState<CoachInterface | null>(null);
  const [coaches, setCoaches] = useState<CoachInterface[]>([]);
  const [confirmStatus, setConfirmStatus] = useState<ConfirmStatus>(ConfirmStatus.None);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [waited, setWaited] = useState<boolean>(false);
  const [showChosenCoach, setShowChosenCoach] = useState<boolean>(false);
  const [showCoaches, setShowCoaches] = useState<number>(10);

  useEffect(() => {
    setTimeout(() => {
      setWaited(true);
    }, 2000);
    api.get("client/matching_coaches").then((response) => {
      setCoaches(response.json.coaches);
      setLoaded(true);
    });
  }, []);

  if (!loaded || !waited) {
    return (
      <div className="mt-43">
        <div className="flex items-center justify-center relative">
          <div className="w-24 h-24 rounded-full flex items-center justify-center bg-indigo-800">
            <div className="flex space-x-2">
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-dot [animation-delay:0s]" />
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-dot [animation-delay:0.2s]" />
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-dot [animation-delay:0.4s]" />
            </div>
          </div>
        </div>
        <h2 className="mt-6 z-heading2 text-center">
          {lang === "sv"
            ? "Matchar dig med coacher"
            : "Matching you with coaches"}
        </h2>
      </div>
    );
  }

  const saveCoach = (profile: ClientProfile) => {
    api.post("client/account/set", profile).then(() => {
      setShowChosenCoach(true);
    });
  };

  const doSelectCoach = () => {
    if (showCoach) {
      const newProfile: ClientProfile = props.profile;
      newProfile.coachId = showCoach.id;
      saveCoach(newProfile);
      setConfirmStatus(ConfirmStatus.Done);
    }
  };

  const closeSelectCoach = () => {
    if (confirmStatus === ConfirmStatus.Done) {
      navigate("/videocoach");
    } else {
      setConfirmStatus(ConfirmStatus.None);
      setSelectCoach(false);
    }
  };

  if (showChosenCoach) {
    return (
      <div className="mt-40 text-center">
        <img
          alt="check"
          src={badgeCheckIcon}
          className="h-12 w-12 text-center mx-auto"
        />
        <h1 className="mt-6 z-heading1 text-center mx-auto text-white">
          {lang === "sv" ? "Coach vald" : "Coach chosen"}
        </h1>
        <p className="mt-2">{lang === "sv" ? "Bra val!" : "Great choice!"}</p>
        <p>
          {lang === "sv" ? "Du har valt " : "You have chosen "}
          {showCoach?.firstName} {showCoach?.lastName}
          {lang === "sv" ? " som din coach. " : " as your coach. "}
          {lang === "sv"
            ? "Du kan ta nästa steg för att nå dina mål och boka ditt första möte."
            : "" +
              "You can take the next step towards achieving your goals and continue with booking your first session."}
        </p>
        <CustomButton
          onClick={() => navigate("/videocoach/book-meeting")}
          buttonType="blueBase"
          className="mt-6"
        >
          {lang === "sv" ? "Boka möte" : "Book a session"}
        </CustomButton>

        <TextLink
          color="white"
          className="mt-4"
          onClick={() => navigate("/videocoach")}
          size="sm"
        >
          {lang === "sv" ? "Jag gör det senare" : "I'll do it later"}
        </TextLink>
      </div>
    );
  }

  const showMoreCoaches = () => {
    let c = showCoaches;
    if (c === 10) {
      c = 15;
    } else if (c === 15) {
      c = 20;
    } else {
      return;
    }
    setShowCoaches(c);
  };

  const renderCoach = () => {
    if (!showCoach) {
      return <></>;
    }
    return (
      <>
        <CoachMatching coach={showCoach} clientProfile={props.profile} />
        <div className="h-20">&nbsp;</div>
        <div className="fixed bottom-0 w-full desktop:w-151 bg-indigo-1000">
          <div className="flex justify-center mt-2">
            <CustomButton onClick={() => setSelectCoach(true)}>
              {t("choose")} {showCoach.firstName} {showCoach.lastName}
            </CustomButton>
          </div>
          <div className="flex justify-center my-4">
            <TextLink
              color="white"
              onClick={() => setShowCoach(null)}
              leftIcon={
                <MaskedIcon src={chevronLeftIcon} className="h-4 w-4" />
              }
              size="sm"
            >
              {t("back")}
            </TextLink>
          </div>
        </div>
        {selectCoach && (
          <Modal isOpen={true} onClose={closeSelectCoach}>
            <ModalHeader onClose={closeSelectCoach}>
              {t("choose")} {showCoach.firstName} {showCoach.lastName}
            </ModalHeader>
            <ModalBody>
              {lang === "sv" ? "Vill du välja " : "Do you want to choose "}
              {showCoach.firstName} {showCoach.lastName}
              {lang === "sv" ? " som din coach" : " as your coach"}?
            </ModalBody>
            <ModalFooter>
              <div className="w-full flex">
                <CustomButton
                  buttonType="outlineWhiteBase"
                  onClick={() => setSelectCoach(false)}
                >
                  {t("cancel")}
                </CustomButton>
                <CustomButton className="ml-4" onClick={doSelectCoach}>
                  {t("choose")}
                </CustomButton>
              </div>
            </ModalFooter>
          </Modal>
        )}
      </>
    );
  };

  if (showCoach) return <>{renderCoach()}</>;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="z-heading2">{t("choose-coach")}</div>

        <div className="mt-2 text-gray-400">
          {t("choose-coach-subheading")}
        </div>
      </div>

      {coaches.slice(0, showCoaches).map((coach) => (
        <CustomCard key={coach.id} onClick={() => setShowCoach(coach)}>
          <CardBody>
            <div className="flex items-center p-4">
              <img
                className="picture-lg"
                src={coach.picture ? coach.picture : iconAccount}
                alt="pic"
              />
              <div className="ml-4">
                <p className="z-heading2">
                  {coach.firstName} {coach.lastName}
                </p>
              </div>
            </div>
          </CardBody>

          <CardFooter>
            <div className="flex flex-col gap-4">
              <p className="text-gray-400 whitespace-pre-line">
                {coach.profile.intro}
              </p>
              <CustomButton
                buttonType="outlineWhiteBase"
                onClick={() => setShowCoach(coach)}
              >
                {t("view-profile")}
              </CustomButton>
            </div>
          </CardFooter>
        </CustomCard>
      ))}

      <div className="text-center">
        {coaches.length > showCoaches ? (
          <TextLink
            color="white"
            onClick={() => showMoreCoaches()}
            leftIcon={<MaskedIcon src={iconPlus} className="h-4 w-4" />}
            size="sm"
          >
            {lang === "sv" ? "Visa fler coacher" : "Show more coaches"}
          </TextLink>
        ) : (
          <p>
            {lang === "sv"
              ? "Du har nått slutet av listan"
              : "You've reached the end of the list"}
          </p>
        )}
      </div>
    </div>
  );
};

export default StepSeven;
